import { useCallback, useState } from "react";
import { ReportPrinter } from "./ReportPrinter";
import moment from "moment";
import { RosterShift, RosterVehicle } from "../pages/roster/types/Roster";
import { useSelector } from "react-redux";
import { RootState } from "../data/store";
import { useDepotFilter } from "../filters/useDepotFilter";
import { Transport } from "../data/api/types/driver";
import { ScheduleDriver } from "../data/api/types/schedule";
import { useTransportFilter } from "../filters/useTransportFilter";
import { PageData, usePrintJourneyListData } from "./usePrintJourneyListData";
import {
  JourneyListSummary,
  processJourneyListSummary,
} from "./printJourneyListHelpers";
import {
  GarageSettings,
  getSettingsByGarage,
  useGarageSettings,
} from "../common/useGarageSettings";
import { electroPage2 } from "./electroPage2";
import { autoPage2 } from "./autoPage2";
import { printLogo } from "./printLogo";
import { printSerialNumber } from "./printSerialNumber";
import { printDrivers } from "./printDrivers";
import { printComments } from "./printComments";
import { getVehicleText, printVehicle } from "./printVehicle";
import { textWithLine } from "./printPrimitives";
import { printAutoDocuments } from "./printAutoDocuments";

async function printPageOne(
  printer: ReportPrinter,
  page: PageData,
  selectedTransport: Transport
) {
  const { addressOffsetX, addressOffsetY } = await printLogo(
    printer,
    selectedTransport
  );

  printer.setFont("slab-light", 10, 1);

  textWithLine(
    printer,
    "Разрешил излизането:",
    (printer.printableWidth * 2) / 3 + 1.5 * printer.pageMarginX + 3,
    42.5,
    { offset: 2.5 }
  );

  printer.setFont("slab-light", 10, 1);
  printer.printTextExt(
    page.depotAddress,
    printer.pageMarginX + addressOffsetX / 2 + 45,
    printer.pageMarginY + addressOffsetY - 3,
    (printer.printableWidth * 2) / 3,
    { align: "center" }
  );

  printComments(printer, page);
  printSerialNumber(printer, page);

  printer.setFont("slab-light", 10, 1.6);
  printer.doc.setLineDashPattern([1, 3], 0);
  printer.printSpace(15);

  const twidth = printVehicle(printer, page, selectedTransport);
  printDrivers(printer, page, selectedTransport, twidth);

  printer.printSpace(0);
  const nameTSize = printer.printText(
    `${moment().format("DD.MM.YYYY")}  Издал пътния лист:`,
    printer.pageMarginX * 2
  );
  // console.log("Issued by:", page.userNames);
  if (page.userNames && page.userNames.trim().length > 0) {
    printer.withFont(
      () => {
        printer.printTextExt(
          page.userNames,
          printer.pageMarginX * 2 + nameTSize.width + 15,
          printer.currentPageY - nameTSize.height + 1
        );
      },
      "slab-bold",
      11,
      1
    );
  }

  printer.printSpace(3);
  const currentY = printer.currentPageY;
  let tsize = printer.printTextExt(
    selectedTransport === "TM" || selectedTransport === "TB"
      ? page.depotName
      : "Подпис:",
    printer.pageMarginX * 2,
    currentY
  );
  printer.doc.line(
    printer.pageMarginX * 2 + tsize.width + 5,
    printer.currentPageY - tsize.height,
    printer.pageMarginX * 2 + 300,
    printer.currentPageY - tsize.height
  );
  tsize = printer.printTextExt(
    `Време на излизане от ${selectedTransport === "A" ? "гаража" : "депото"}:`,
    printer.pageMarginX * 2 + 320,
    currentY
  );
  let firstShiftStart = printer.pageMarginX * 2 + 320 + tsize.width + 5;
  const tsize1 = printer.printTextExt("I см.:", firstShiftStart, currentY);
  printer.doc.line(
    firstShiftStart + tsize1.width + 5,
    printer.currentPageY - tsize1.height,
    firstShiftStart + 90,
    printer.currentPageY - tsize1.height
  );
  const tsize2 = printer.printTextExt(
    "II см.:",
    firstShiftStart + 100,
    currentY
  );
  printer.doc.line(
    firstShiftStart + tsize2.width + 105,
    printer.currentPageY - tsize2.height,
    firstShiftStart + 190,
    printer.currentPageY - tsize2.height
  );
  const tsize3 = printer.printTextExt(
    "III см.:",
    firstShiftStart + 200,
    currentY
  );
  printer.doc.line(
    firstShiftStart + tsize3.width + 205,
    printer.currentPageY - tsize3.height,
    printer.printableWidth,
    printer.currentPageY - tsize3.height
  );

  //console.log("print times:", shift1Times, shift2Times, shift3Times);

  printer.withFont(
    () => {
      if (page.drivers.shift1?.shiftTimes?.exitTime) {
        printer.doc.text(
          page.drivers.shift1.shiftTimes.exitTime.format("HH:mm"),
          firstShiftStart + tsize1.width + 15,
          printer.currentPageY - tsize1.height - 2
        );
      }

      if (page.drivers.shift2?.shiftTimes?.exitTime) {
        printer.doc.text(
          page.drivers.shift2.shiftTimes.exitTime.format("HH:mm"),
          firstShiftStart + tsize2.width + 115,
          printer.currentPageY - tsize2.height - 2
        );
      }

      if (page.drivers.shift3?.shiftTimes?.exitTime) {
        printer.doc.text(
          page.drivers.shift3.shiftTimes.exitTime.format("HH:mm"),
          firstShiftStart + tsize3.width + 215,
          printer.currentPageY - tsize3.height - 2
        );
      }
    },
    "slab-bold",
    13,
    1
  );

  tsize = printer.printText(
    `Време на прибиране в ${selectedTransport === "A" ? "гаража" : "депото"}:`,
    printer.pageMarginX * 2 + 320
  );

  printer.doc.line(
    firstShiftStart + tsize1.width + 5,
    printer.currentPageY - tsize1.height,
    firstShiftStart + 90,
    printer.currentPageY - tsize1.height
  );
  printer.doc.line(
    firstShiftStart + tsize2.width + 105,
    printer.currentPageY - tsize2.height,
    firstShiftStart + 190,
    printer.currentPageY - tsize2.height
  );
  printer.doc.line(
    firstShiftStart + tsize3.width + 205,
    printer.currentPageY - tsize3.height,
    printer.printableWidth,
    printer.currentPageY - tsize3.height
  );

  printer.withFont(
    () => {
      if (page.drivers.shift1?.shiftTimes?.enterTime) {
        printer.doc.text(
          page.drivers.shift1.shiftTimes.enterTime.format("HH:mm"),
          firstShiftStart + tsize1.width + 15,
          printer.currentPageY - tsize1.height - 2
        );
      }

      if (page.drivers.shift2?.shiftTimes?.enterTime) {
        printer.doc.text(
          page.drivers.shift2.shiftTimes.enterTime.format("HH:mm"),
          firstShiftStart + tsize2.width + 115,
          printer.currentPageY - tsize2.height - 2
        );
      }

      if (page.drivers.shift3?.shiftTimes?.enterTime) {
        printer.doc.text(
          page.drivers.shift3.shiftTimes.enterTime.format("HH:mm"),
          firstShiftStart + tsize3.width + 215,
          printer.currentPageY - tsize3.height - 2
        );
      }
    },
    "slab-bold",
    13,
    1
  );

  printer.printSpace(-10);
  printer.doc.setLineDashPattern([], 0);
  const line1Y = printer.currentPageY;
  printer.doc.line(
    printer.pageMarginX * 2,
    line1Y,
    printer.printableWidth,
    line1Y
  );

  printer.printSpace(13);
  tsize = printer.printText(
    `${getVehicleText(page, selectedTransport, 2)} е технически изправен.`,
    printer.pageMarginX * 2
  );
  printer.printTextExt(
    `Приех ${getVehicleText(page, selectedTransport, 3)} технически изправен.`,
    printer.pageMarginX * 2 + printer.printableWidth / 2,
    printer.currentPageY - tsize.height
  );

  printer.printSpace(3);
  tsize = printer.printText(
    selectedTransport === "TM" || selectedTransport === "TB"
      ? "Дежурен депо:"
      : "Механик:",
    printer.pageMarginX * 2
  );
  printer.doc.setLineDashPattern([1, 3], 0);
  printer.doc.line(
    printer.pageMarginX * 2 + tsize.width + 5,
    printer.currentPageY - tsize1.height,
    printer.pageMarginX + printer.printableWidth / 2 - 30,
    printer.currentPageY - tsize1.height
  );
  printer.withFont(
    () => {
      printer.doc.text(
        "(фамилия, подпис)",
        printer.pageMarginX * 2 +
        tsize.width +
        2 +
        (printer.pageMarginX +
          printer.printableWidth / 2 -
          30 -
          (printer.pageMarginX * 2 + tsize.width + 5)) /
        2,
        printer.currentPageY - tsize.height / 2,
        { align: "center" }
      );
    },
    "slab-light",
    8
  );
  printer.printSpace(-tsize.height);

  tsize = printer.printText(
    selectedTransport === "A" ? "Водач:" : "Водачи:",
    printer.pageMarginX * 2 + printer.printableWidth / 2
  );
  printer.doc.setLineDashPattern([1, 3], 0);
  printer.doc.line(
    printer.pageMarginX * 2 + printer.printableWidth / 2 + tsize.width + 5,
    printer.currentPageY - tsize1.height,
    printer.printableWidth,
    printer.currentPageY - tsize1.height
  );
  if (selectedTransport === "A") {
    printer.withFont(
      () => {
        printer.doc.text(
          "(фамилия, подпис)",
          printer.pageMarginX * 2 +
          printer.printableWidth / 2 +
          tsize.width +
          2 +
          (printer.printableWidth -
            (printer.pageMarginX * 2 +
              printer.printableWidth / 2 +
              tsize.width +
              2)) /
          2,
          printer.currentPageY - tsize.height / 2,
          { align: "center" }
        );
      },
      "slab-light",
      8
    );
  } else {
    const center =
      printer.pageMarginX * 2 +
      printer.printableWidth / 2 +
      tsize.width +
      2 +
      (printer.printableWidth -
        (printer.pageMarginX * 2 +
          printer.printableWidth / 2 +
          tsize.width +
          2)) /
      2;
    printer.doc.text("/", center, printer.currentPageY - tsize.height - 2, {
      align: "center",
    });
    printer.withFont(
      () => {
        printer.doc.text(
          "(I см. - фамилия, подпис)                          (II см. - фамилия, подпис)",
          center,
          printer.currentPageY - tsize.height / 2,
          { align: "center" }
        );
      },
      "slab-light",
      8
    );
  }

  printer.printSpace(-3);
  printer.doc.setLineDashPattern([], 0);
  printer.doc.line(
    printer.pageMarginX + printer.printableWidth / 2,
    line1Y,
    printer.pageMarginX + printer.printableWidth / 2,
    printer.currentPageY
  );
  printer.doc.line(
    printer.pageMarginX * 2,
    printer.currentPageY,
    printer.printableWidth,
    printer.currentPageY
  );
  const line2Y = printer.currentPageY;

  if (selectedTransport !== "TM" && selectedTransport !== "TB") {
    printer.printSpace(13);
    if (selectedTransport === "A") {
      tsize = printer.printText(
        "Проба за алкохол. Общо здравословно състояние - добро.",
        printer.pageMarginX * 2
      );

      tsize = printer.printTextExt(
        "Водачът е годен да управлява.",
        printer.pageMarginX * 2 + printer.printableWidth / 2,
        printer.currentPageY - tsize.height
      );
    } else {
      tsize = printer.printText("Проба за алкохол.", printer.pageMarginX * 2);

      tsize = printer.printTextExt(
        "Общо здравословно състояние - добро.",
        printer.pageMarginX * 2 + printer.printableWidth / 2,
        printer.currentPageY - tsize.height
      );

      tsize = printer.printTextExt(
        "Водачът е годен да управлява.",
        printer.pageMarginX * 2 + printer.printableWidth / 2,
        printer.currentPageY
      );

      printer.printSpace(-tsize.height);
    }

    printer.doc.setLineDashPattern([1, 3], 0);
    tsize = printer.printText("ИЗЛИЗАНЕ", printer.pageMarginX * 2);
    firstShiftStart = printer.pageMarginX * 2 + tsize.width + 30;
    tsize = printer.printTextExt(
      "I  СМЯНА:",
      firstShiftStart,
      printer.currentPageY - tsize.height
    );
    printer.doc.line(
      firstShiftStart + tsize.width + 5,
      printer.currentPageY - tsize1.height,
      firstShiftStart + 125,
      printer.currentPageY - tsize1.height
    );
    tsize = printer.printTextExt(
      "II  СМЯНА:",
      firstShiftStart + 135,
      printer.currentPageY - tsize.height
    );
    printer.doc.line(
      firstShiftStart + 135 + tsize.width + 5,
      printer.currentPageY - tsize1.height,
      printer.pageMarginX + printer.printableWidth / 2 - 15,
      printer.currentPageY - tsize1.height
    );

    tsize = printer.printText("ПРИБИРАНЕ", printer.pageMarginX * 2);
    tsize = printer.printTextExt(
      "I  СМЯНА:",
      firstShiftStart,
      printer.currentPageY - tsize.height
    );
    printer.doc.line(
      firstShiftStart + tsize.width + 5,
      printer.currentPageY - tsize1.height,
      firstShiftStart + 125,
      printer.currentPageY - tsize1.height
    );
    tsize = printer.printTextExt(
      "II  СМЯНА:",
      firstShiftStart + 135,
      printer.currentPageY - tsize.height
    );
    printer.doc.line(
      firstShiftStart + 135 + tsize.width + 5,
      printer.currentPageY - tsize1.height,
      printer.pageMarginX + printer.printableWidth / 2 - 15,
      printer.currentPageY - tsize1.height
    );

    tsize = printer.printTextExt(
      "Извършил проверката:",
      printer.pageMarginX * 2 + printer.printableWidth / 2,
      printer.currentPageY - tsize.height
    );
    printer.doc.line(
      printer.pageMarginX * 2 + printer.printableWidth / 2 + tsize.width + 5,
      printer.currentPageY - tsize.height,
      printer.printableWidth,
      printer.currentPageY - tsize.height
    );
    printer.withFont(
      () => {
        printer.doc.text(
          "(фамилия, подпис)",
          printer.pageMarginX * 2 +
          printer.printableWidth / 2 +
          tsize.width +
          2 +
          (printer.printableWidth -
            (printer.pageMarginX * 2 +
              printer.printableWidth / 2 +
              tsize.width +
              2)) /
          2,
          printer.currentPageY - tsize.height / 2,
          { align: "center" }
        );
      },
      "slab-light",
      8
    );

    printer.printSpace(-4);
    printer.doc.setLineDashPattern([], 0);
    printer.doc.line(
      printer.pageMarginX + printer.printableWidth / 2,
      line2Y,
      printer.pageMarginX + printer.printableWidth / 2,
      printer.currentPageY
    );
    printer.doc.line(
      printer.pageMarginX * 2,
      printer.currentPageY,
      printer.printableWidth,
      printer.currentPageY
    );

    const line3Y = printer.currentPageY;

    printer.printSpace(13);
    tsize = printer.printText("Километропоказател:", printer.pageMarginX * 2);
    tsize = printer.printTextExt(
      "Заредено гориво:",
      printer.pageMarginX * 2 + printer.printableWidth / 2,
      printer.currentPageY - tsize.height
    );

    printer.doc.setLineDashPattern([1, 3], 0);
    tsize = printer.printText("Начален:", printer.pageMarginX * 2);
    printer.doc.line(
      printer.pageMarginX * 2 + tsize.width + 5,
      printer.currentPageY - tsize1.height,
      printer.pageMarginX * 2 + printer.printableWidth / 4 - 25,
      printer.currentPageY - tsize1.height
    );
    tsize = printer.printTextExt(
      "Краен:",
      printer.pageMarginX * 2 + printer.printableWidth / 4 - 10,
      printer.currentPageY - tsize.height
    );
    printer.doc.line(
      printer.pageMarginX * 2 +
      printer.printableWidth / 4 -
      10 +
      tsize.width +
      5,
      printer.currentPageY - tsize1.height,
      printer.pageMarginX + printer.printableWidth / 2 - 15,
      printer.currentPageY - tsize1.height
    );

    const lSize = printer.getWidth("л.");

    tsize = printer.printTextExt(
      "Първа смяна:",
      printer.pageMarginX * 2 + printer.printableWidth / 2,
      printer.currentPageY - tsize1.height
    );
    printer.doc.line(
      printer.pageMarginX * 2 + printer.printableWidth / 2 + tsize.width + 5,
      printer.currentPageY - tsize1.height,
      printer.pageMarginX * 2 +
      printer.printableWidth / 2 +
      printer.printableWidth / 4 -
      25 -
      lSize -
      3,
      printer.currentPageY - tsize1.height
    );
    printer.doc.text(
      "л.",
      printer.pageMarginX * 2 +
      printer.printableWidth / 2 +
      printer.printableWidth / 4 -
      25 -
      lSize,
      printer.currentPageY - tsize1.height
    );
    tsize = printer.printTextExt(
      "Втора смяна:",
      printer.pageMarginX * 2 +
      printer.printableWidth / 2 +
      printer.printableWidth / 4 -
      10,
      printer.currentPageY - tsize.height
    );
    printer.doc.line(
      printer.pageMarginX * 2 +
      printer.printableWidth / 2 +
      printer.printableWidth / 4 -
      10 +
      tsize.width +
      5,
      printer.currentPageY - tsize1.height,
      printer.printableWidth - lSize - 10,
      printer.currentPageY - tsize1.height
    );
    printer.doc.text(
      "л.",
      printer.printableWidth - lSize - 7,
      printer.currentPageY - tsize1.height
    );

    printer.printSpace(-8);
    printer.doc.setLineDashPattern([], 0);
    printer.doc.line(
      printer.pageMarginX + printer.printableWidth / 2,
      line3Y,
      printer.pageMarginX + printer.printableWidth / 2,
      printer.currentPageY
    );
    printer.doc.line(
      printer.pageMarginX * 2,
      printer.currentPageY,
      printer.printableWidth,
      printer.currentPageY
    );
  }

  printer.printSpace(16);
  printer.doc.setLineDashPattern([1, 3], 0);
  tsize = printer.printText(
    "Приел и проверил пътния лист:",
    printer.pageMarginX * 2
  );
  printer.printTextExt(
    "/",
    printer.printableWidth - 275,
    printer.currentPageY - tsize.height
  );
  printer.printTextExt(
    "/",
    printer.printableWidth - 125,
    printer.currentPageY - tsize.height
  );

  printer.doc.line(
    printer.pageMarginX * 2 + tsize.width + 5,
    printer.currentPageY - tsize.height,
    printer.printableWidth,
    printer.currentPageY - tsize.height
  );

  printer.withFont(
    () => {
      printer.doc.text(
        "(име, фамилия)",
        printer.pageMarginX * 2 +
        tsize.width +
        5 +
        (printer.printableWidth -
          275 -
          (printer.pageMarginX * 2 + tsize.width + 5)) /
        2,
        printer.currentPageY - tsize.height / 2,
        { align: "center" }
      );

      printer.doc.text(
        "(дата)",
        printer.printableWidth -
        275 +
        (printer.printableWidth - 125 - (printer.printableWidth - 275)) / 2,
        printer.currentPageY - tsize.height / 2,
        { align: "center" }
      );

      printer.doc.text(
        "(подпис)",
        printer.printableWidth -
        125 +
        (printer.printableWidth - (printer.printableWidth - 125)) / 2,
        printer.currentPageY - tsize.height / 2,
        { align: "center" }
      );
    },
    "slab-light",
    8
  );

  printer.printSpace(5);

  if (selectedTransport === "TM" || selectedTransport === "TB") {
    printer.doc.setLineDashPattern([], 0);
    printer.doc.line(
      printer.pageMarginX * 2,
      printer.currentPageY,
      printer.printableWidth,
      printer.currentPageY
    );
    printer.printSpace(20);

    printer.printText(
      "Колата спряла на линия от ....................... ч. до ....................... ч.; от ....................... ч. до ....................... ч.; от ....................... ч. до ....................... ч.; от ...................... ч. до ...................... ч.",
      printer.pageMarginX * 2
    );
    printer.printText(
      "Колата прибрана в депо в ................ ч. излязла в ................ ч.; в ................ ч. излязла в ................ ч.; в ................ ч.; излязла в ............... ч.; в ............... ч. излязла в ............... ч.",
      printer.pageMarginX * 2
    );
    printer.printText(
      "Колата заменена с кола инв. № ............................................ в .................... ч.; с кола № ............................................ в .................... ч.; с кола № ............................................ в .................... ч.",
      printer.pageMarginX * 2
    );

    let tsize = printer.printText("Причини:", printer.pageMarginX * 2);

    printer.doc.setLineDashPattern([1, 3], 0);

    printer.doc.line(
      printer.pageMarginX * 2 + tsize.width + 5,
      printer.currentPageY - tsize.height,
      printer.printableWidth,
      printer.currentPageY - tsize.height
    );

    for (let i = 0; i < 7; ++i) {
      printer.printText(" ", printer.pageMarginX * 2);

      printer.doc.line(
        printer.pageMarginX * 2,
        printer.currentPageY - tsize.height,
        printer.printableWidth,
        printer.currentPageY - tsize.height
      );
    }

    printer.printSpace(10);

    tsize = printer.printText(
      "Дежурен ръководител:",
      printer.pageMarginX * 2 + printer.printableWidth / 2
    );
    printer.doc.line(
      printer.pageMarginX * 2 + printer.printableWidth / 2 + tsize.width + 5,
      printer.currentPageY - tsize.height,
      printer.printableWidth,
      printer.currentPageY - tsize.height
    );
  } else {
    printAutoDocuments(printer, page, selectedTransport);

    printer.printSpace(10);
    printer.withFont(
      () => {
        printer.printText(
          "Приложение 11 към чл.66 т.7/ДВ. бр.13 от 11.02.2011г.",
          printer.printableWidth,
          "unlimited",
          { align: "right" }
        );
      },
      "slab-light",
      9,
      1
    );
  }

  printer.newPage();
}

async function printPageTwo(
  printer: ReportPrinter,
  page: PageData,
  garageSettings: GarageSettings,
  selectedTransport: Transport
) {
  if (selectedTransport === "TM" || selectedTransport === "TB") {
    await electroPage2(printer, page, garageSettings);
  } else {
    await autoPage2(printer);
  }
}

function printSummary(
  printer: ReportPrinter,
  summary: JourneyListSummary[],
  page: PageData
) {
  printer.newPage();

  printer.setFont("bold", 12, 1.2);
  // printer.printSpace(12);
  printer.printText(
    page.depotName,
    printer.pageMarginX + printer.printableWidth / 2,
    "page",
    { align: "center" }
  );

  const tableStatus = {
    currentSide: 0,
    topMargin: printer.currentPageY,
  };

  printer.autoTable({
    //tableWidth: printer.printableWidth / 2 - 15,
    margin: {
      top: printer.pageMarginY,
      bottom: printer.pageMarginY,
      left: printer.pageMarginX,
      right: printer.pageMarginX + printer.printableWidth / 2 + 15,
    },
    styles: {
      valign: "middle",
      font: "Roboto",
      fontSize: 8,
      cellPadding: 4,
      lineColor: [0, 0, 0],
      lineWidth: 0.25,
    },
    columnStyles: {
      0: { cellWidth: 65, halign: "left" },
      1: { cellWidth: 35, halign: "left" },
      2: { cellWidth: 40, halign: "left" },
      3: { cellWidth: 40, halign: "left" },
      4: { halign: "right" },
    },
    head: [
      [
        { content: "Линия №", styles: { halign: "left" } },
        { content: "Кола", styles: { halign: "left" } },
        { content: "Смяна", styles: { halign: "left" } },
        { content: "Автобус", styles: { halign: "left" } },
        { content: "Пътен лист №", styles: { halign: "right" } },
      ],
    ],
    body: summary.map((s) => [
      { content: s.line },
      { content: s.car },
      { content: s.shift },
      { content: s.vehicle },
      { content: s.serial.substring(0, s.serial.indexOf(" ")) },
    ]),
    didDrawPage: (data) => {
      if (tableStatus.currentSide === 0) {
        data.settings.margin = {
          top: tableStatus.topMargin,
          bottom: printer.pageMarginY,
          left: printer.pageMarginX + printer.printableWidth / 2 + 15,
          right: printer.pageMarginX,
        };
        printer.doc.setPage(printer.doc.getCurrentPageInfo().pageNumber - 1);
        tableStatus.currentSide = 1;
        tableStatus.topMargin = printer.pageMarginY;
      } else {
        data.settings.margin = {
          top: tableStatus.topMargin,
          bottom: printer.pageMarginY,
          left: printer.pageMarginX,
          right: printer.pageMarginX + printer.printableWidth / 2 + 15,
        };
        tableStatus.currentSide = 0;
        tableStatus.topMargin = printer.pageMarginY;
      }
    },
  });

  printer.doc.deletePage(1);
}

async function printSingle(
  printer: ReportPrinter,
  page: PageData,
  selectedTransport: Transport,
  garageSettings: GarageSettings
) {
  await printPageOne(printer, page, selectedTransport);
  await printPageTwo(printer, page, garageSettings, selectedTransport);
}

export function usePrintBlankJourneyList() {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDone, setIsDone] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  const selectedAutoColumn = useSelector(
    (state: RootState) => state.filters.selectedAutoColumn
  );
  const { depots, selectedDepot } = useDepotFilter();
  const { selectedTransport } = useTransportFilter();

  const processData = usePrintJourneyListData();
  const userGarageSettings = useGarageSettings();
  const garageSettings = getSettingsByGarage(selectedTransport, selectedDepot);

  const exportBlankPdf = useCallback(
    async (rosterDate: string, includePowerUsage?: boolean) => {
      if (selectedTransport === "") {
        setIsDone(true);
        setHasError(true);
        setIsLoading(false);
        throw "Transport is not selected";
      } else {
        setIsDone(false);
        setHasError(false);
        setIsLoading(true);

        try {
          const printer = new ReportPrinter({
            orientation: "landscape",
            printPageNumber: false,
            printDate: false,
            printMusat: false,
            marginX: 20,
            marginY: 20,
            lineSpacing: 1,
          });

          const data = await processData.emptyData({
            rosterDate,
            includeEnergyInEmpty: includePowerUsage,
          });

          await printSingle(printer, data, selectedTransport, garageSettings);

          printer.save(
            `Пътен-лист-${rosterDate}-${depots.find((d) => d.id === selectedDepot)?.value || "-"
            }-${selectedAutoColumn}-${moment().format(
              "YYYY-MM-DD-HH-mm-ss"
            )}.pdf`
          );

          return printer;
        } catch (err) {
          console.error("Cannot export into PDF:", err);
          setHasError(true);
          throw err;
        } finally {
          setIsDone(true);
          setIsLoading(false);
        }
      }
    },
    [
      depots,
      garageSettings,
      processData,
      selectedAutoColumn,
      selectedDepot,
      selectedTransport,
    ]
  );

  const exportPdf = useCallback(
    async (
      vehicles: readonly RosterVehicle[],
      // drivers: readonly Driver[],
      drivers: readonly ScheduleDriver[],
      extraDrivers: ScheduleDriver[],
      //apiVehicles: readonly Vehicle[],
      shifts: readonly RosterShift[],
      rosterDate: string,
      includeEmptyVehicles?: boolean
    ) => {
      console.log("Export PDF");

      if (selectedTransport === "") {
        setIsDone(true);
        setHasError(true);
        setIsLoading(false);
        throw "Transport is not selected";
      } else {
        setIsDone(false);
        setHasError(false);
        setIsLoading(true);

        try {
          const data = await processData.processData({
            rosterDate: rosterDate,
            includeEmptyVehicles: !!includeEmptyVehicles,
            vehicles: vehicles,
            extraDrivers: extraDrivers,
            drivers: drivers,
            shifts: shifts,
          });

          const printer = new ReportPrinter({
            orientation: "landscape",
            printPageNumber: false,
            printDate: false,
            printMusat: false,
            marginX: 20,
            marginY: 20,
            lineSpacing: 1,
          });

          let isFirstPage = true;

          for (const page of data) {
            if (!isFirstPage) {
              printer.doc.addPage();
            }

            await printSingle(printer, page, selectedTransport, garageSettings);

            isFirstPage = false;
          }

          printer.save(
            `Пътен-лист-${rosterDate}-${depots.find((d) => d.id === selectedDepot)?.value || "-"
            }-${selectedAutoColumn}-${moment().format(
              "YYYY-MM-DD-HH-mm-ss"
            )}.pdf`
          );

          if (data.length > 0 && userGarageSettings.printJourneyListSummary) {
            // Печат на справка за пътните листа само за Дружба
            const rep_printer = new ReportPrinter({
              orientation: "portrait",
              printPageNumber: true,
              pageNumberOffset: -1,
              printDate: true,
              printMusat: true,
              marginX: 30,
              marginY: 50,
              lineSpacing: 1,
              headerText: `РАЗДАДЕНИ ПЪТНИ ЛИСТА НА НАРЯД ЗА ДАТА: ${moment(
                rosterDate,
                "YYYY-MM-DD"
              ).format("L")}`,
            });

            const summary = [...processJourneyListSummary(data)];
            printSummary(rep_printer, summary, data[0]);

            rep_printer.save(
              `Раздадени-пътни-листа-${rosterDate}-${depots.find((d) => d.id === selectedDepot)?.value || "-"
              }-${selectedAutoColumn}-${moment().format(
                "YYYY-MM-DD-HH-mm-ss"
              )}.pdf`
            );
          }

          return printer;
        } catch (err) {
          console.error("Cannot export into PDF:", err);
          setHasError(true);
          throw err;
        } finally {
          setIsDone(true);
          setIsLoading(false);
        }
      }
    },
    [
      depots,
      userGarageSettings.printJourneyListSummary,
      garageSettings,
      processData,
      selectedAutoColumn,
      selectedDepot,
      selectedTransport,
    ]
  );

  return {
    isLoading,
    isDone,
    hasError,
    exportPdf,
    exportBlankPdf,
  };
}
